import React, { useEffect, useRef, useState } from "react";
import { Trophy, Sparkle, X, Confetti, Star, FlagBanner } from "@phosphor-icons/react";

/**
 * Watches an observations list and fires celebratory toasts on milestones:
 *  - First resolution accepted
 *  - 50% cycle completion
 *  - 100% cycle completion
 *  - All high-risk findings resolved
 */
export default function AchievementToast({ observations = [], cycleId }) {
  const [active, setActive] = useState(null); // { id, title, message, icon }
  const seenRef = useRef(new Set());

  // Load seen achievements for this cycle from localStorage
  useEffect(() => {
    if (!cycleId) return;
    const raw = localStorage.getItem(`ach_seen_${cycleId}`);
    seenRef.current = new Set(raw ? JSON.parse(raw) : []);
  }, [cycleId]);

  const persistSeen = () => {
    if (!cycleId) return;
    localStorage.setItem(`ach_seen_${cycleId}`, JSON.stringify([...seenRef.current]));
  };

  const fire = (achievement) => {
    if (seenRef.current.has(achievement.id)) return;
    seenRef.current.add(achievement.id);
    persistSeen();
    setActive(achievement);
    // auto-dismiss after 6s
    setTimeout(() => setActive(null), 6000);
  };

  useEffect(() => {
    if (!observations.length || !cycleId) return;

    const total = observations.length;
    const resolved = observations.filter(
      (o) => o.status === "Resolved" || o.status === "Closed"
    ).length;
    const pct = (resolved / total) * 100;

    const highs = observations.filter((o) => o.risk === "High");
    const highResolvedCount = highs.filter(
      (o) => o.status === "Resolved" || o.status === "Closed"
    ).length;

    // 1) First resolution
    if (resolved >= 1) {
      fire({
        id: "first-resolution",
        title: "First Resolution Logged",
        message: "Your first observation has been resolved this cycle. Momentum starts here.",
        icon: <Star size={32} weight="fill" />,
        tone: "from-blue-500 to-blue-700",
      });
    }

    // 2) 50%
    if (pct >= 50 && pct < 100) {
      fire({
        id: "halfway",
        title: "Halfway There",
        message: `You've resolved ${resolved} of ${total} observations. Keep going!`,
        icon: <FlagBanner size={32} weight="fill" />,
        tone: "from-amber-500 to-amber-700",
      });
    }

    // 3) All high risks resolved
    if (highs.length > 0 && highResolvedCount === highs.length) {
      fire({
        id: "high-cleared",
        title: "All High-Risk Findings Cleared",
        message: `${highs.length} high-risk observation(s) are now resolved. Major risk reduction achieved.`,
        icon: <Sparkle size={32} weight="fill" />,
        tone: "from-emerald-500 to-emerald-700",
      });
    }

    // 4) 100%
    if (pct >= 100) {
      fire({
        id: "complete",
        title: "Cycle 100% Complete!",
        message: `Every observation in this cycle is resolved. Outstanding work.`,
        icon: <Trophy size={32} weight="fill" />,
        tone: "from-emerald-500 via-teal-500 to-blue-600",
        confetti: true,
      });
    }
  }, [observations, cycleId]);

  if (!active) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm" data-testid="achievement-toast">
      {active.confetti && <ConfettiPiece />}
      <div className="pop-in bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden">
        <div className={`bg-gradient-to-br ${active.tone} p-4 text-white relative`}>
          <button
            onClick={() => setActive(null)}
            className="absolute top-2 right-2 text-white/80 hover:text-white p-1 rounded"
            data-testid="achievement-close"
          >
            <X size={14} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/15 flex items-center justify-center backdrop-blur">
              {active.icon}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">
                Achievement Unlocked
              </div>
              <div className="text-base font-bold">{active.title}</div>
            </div>
          </div>
        </div>
        <div className="p-4 text-sm text-slate-700">{active.message}</div>
      </div>
    </div>
  );
}

/** Tiny CSS-only confetti burst when 100% is hit. */
function ConfettiPiece() {
  const pieces = Array.from({ length: 30 });
  return (
    <div className="pointer-events-none fixed inset-0 z-40 overflow-hidden" data-testid="confetti">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.4;
        const duration = 2.4 + Math.random() * 1.2;
        const color = ["#0B2A5B", "#2563EB", "#A8B86C", "#F59E0B", "#10B981"][i % 5];
        const rotation = Math.random() * 360;
        return (
          <span
            // static confetti burst — list size + order never change for the lifetime of the toast,
            // so index-based key is intentional and safe.
            key={`confetti-${i}`}
            className="absolute top-0 w-2 h-3 confetti-piece"
            style={{
              left: `${left}%`,
              backgroundColor: color,
              animationDelay: `${delay}s`,
              animationDuration: `${duration}s`,
              transform: `rotate(${rotation}deg)`,
            }}
          />
        );
      })}
    </div>
  );
}
